import { type ReactNode } from 'react';
import { DropdownMenu, type DropdownMenuItem } from './DropdownMenu';
import { PopoverMenu } from './PopoverMenu';
import chevronDownWhiteIcon from './icons/chevron-down-white.svg';
import gridDotsWhiteIcon from './icons/grid-dots-white.svg';
import menuWhiteIcon from './icons/menu-white.svg';
import searchGrayIcon from './icons/search-gray.svg';
import type { AppSelectorInfo } from './sidebar-types';
import { cn } from '@/lib/cn';

interface HeaderProps {
  isOpened: boolean;
  label?: ReactNode;
  appSelectorInfo?: AppSelectorInfo;
  minimizable: boolean;
  handleChangeOpen: () => void;
  headerMenuItems?: DropdownMenuItem[];
  search: string;
  headerMenuSelectedItem?: DropdownMenuItem & { labelCurrent?: ReactNode };
  isHeaderMenuItemsLoading?: boolean;
  disabledHeaderMenu?: boolean;
  resolveItemsCount: number;
  searchField?: boolean;
  setSearch: (value: string) => void;
}

export default function Header({
  label,
  search,
  isOpened,
  setSearch,
  minimizable,
  searchField,
  appSelectorInfo,
  headerMenuItems,
  handleChangeOpen,
  headerMenuSelectedItem,
  isHeaderMenuItemsLoading,
  disabledHeaderMenu,
  resolveItemsCount,
}: HeaderProps) {
  return (
    <div
      className={cn(
        'w-full flex flex-col items-center justify-center pt-4 border-white border-opacity-12',
        searchField ? 'space-y-2' : 'border-b px-4 pb-6 space-y-6',
      )}
    >
      <div
        className={cn('flex w-full items-center justify-between', {
          'border-b border-[rgba(255,_255,_255,_0.12)] px-4 pb-3': searchField,
        })}
      >
        {isOpened && <div className="min-w-0 flex-1">{label}</div>}
        <div className="flex h-6 flex-row items-center gap-4">
          {isOpened && appSelectorInfo && (
            <PopoverMenu
              placement="bottom-start"
              classes={{
                container: '!mt-0 !w-auto z-10',
                panel: 'w-[230px] p-0 !left-auto !right-[-50px]',
              }}
              button={<img src={gridDotsWhiteIcon} alt="app selector" className="select-none" />}
              items={appSelectorInfo.items}
            />
          )}
          {minimizable && (
            <button type="button" onClick={handleChangeOpen} aria-label="Toggle sidebar">
              <img src={menuWhiteIcon} alt="Menu" className="select-none" />
            </button>
          )}
        </div>
      </div>

      {headerMenuItems && (
        <DropdownMenu
          items={headerMenuItems}
          className="w-[280px] border shadow-lg"
          selectedValue={headerMenuSelectedItem?.value}
          loading={isHeaderMenuItemsLoading}
          disabled={disabledHeaderMenu}
          button={
            <div
              className={cn('flex items-center', {
                'justify-between': isOpened,
                'justify-center': !isOpened,
                'w-full': !searchField,
                'w-[calc(100%-32px)] pb-2': searchField,
              })}
            >
              <div className="flex items-center">
                {isOpened && (
                  <div className="text-[14px] leading-[22px]">
                    {isHeaderMenuItemsLoading ? (
                      <div className="mt-1 h-4 w-[140px] animate-pulse rounded bg-white/20" />
                    ) : (
                      headerMenuSelectedItem?.labelCurrent || headerMenuSelectedItem?.label
                    )}
                  </div>
                )}
              </div>
              {isOpened && !disabledHeaderMenu && !isHeaderMenuItemsLoading && (
                <div>
                  <img
                    className="h-4 w-4 min-h-4 min-w-4 select-none"
                    src={chevronDownWhiteIcon}
                    alt="Open register menu"
                  />
                </div>
              )}
            </div>
          }
        />
      )}

      {searchField && (
        <label className="flex h-10 w-[calc(100%-16px)] select-none items-center rounded-md border border-[rgba(255,_255,_255,_0.12)] bg-[#171717] p-3 text-white hover:border-gray-500 focus-within:border-[rgba(255,_255,_255,_0.12)]">
          <img className="mr-2" src={searchGrayIcon} alt="Search Icon" />
          <input
            value={search}
            placeholder="Search page"
            onChange={e => setSearch(e.target.value)}
            className="!w-full flex-1 bg-transparent text-sm text-white outline-none ring-0 placeholder:text-gray-400 focus:outline-none focus:ring-0"
          />
        </label>
      )}

      {search && (
        <div className="self-start px-4 text-xs text-gray-500">
          {resolveItemsCount
            ? `${resolveItemsCount} result${resolveItemsCount === 1 ? '' : 's'}`
            : 'No results'}
        </div>
      )}
    </div>
  );
}
